import classNames from 'classnames/bind';
import SemanticDropdown from 'mcs-toolkit/components/Dropdown';
import React, { Fragment } from 'react';
import { DropdownItemProps, DropdownProps } from 'semantic-ui-react';

import styles from './Dropdown.css';

const css = classNames.bind(styles);

export type DropdownExtendedProps = DropdownProps & {
    selectedValueCustomRender?: (value: any) => React.ReactNode;
    options?: DropdownOption[];
    excludedKeys?: string[];
    dataTest?: string;
};

export type DropdownOption = DropdownItemProps & {
    items?: DropdownItemProps[];
};

export type DropdownChangeHandler = (
    event: React.SyntheticEvent<HTMLElement>,
    props: DropdownExtendedProps,
) => void;

export class Dropdown extends React.PureComponent<DropdownExtendedProps> {
    _handleClick = (e, item) => {
        if (item.disabled) {
            return;
        }

        const { onChange } = this.props;
        onChange && onChange(e, { ...this.props, value: item.value });
    };

    _defaultTriggerRender = (selectedValue) => {
        const { options, placeholder, dataTest } = this.props;

        if (!options) {
            throw 'no options provided';
        }

        let text = '' as any;

        options.forEach((option) => {
            if (option.value && option.value === selectedValue) {
                text = option.text;
            } else if (option.items) {
                option.items.forEach((item) => {
                    if (item.value === selectedValue) {
                        text = item.text;
                    }
                });
            }
        });

        if (!text && !selectedValue && placeholder) {
            text = placeholder;
        }

        return (
            <div data-test={dataTest} className={css('truncation')}>
                {text}
            </div>
        );
    };

    _renderTrigger = (selectedValue) => {
        const triggerRenderer =
            this.props.selectedValueCustomRender || this._defaultTriggerRender;
        return triggerRenderer(selectedValue);
    };

    _renderSimpleItem = (
        itemProps: DropdownOption,
        { index, header }: { header?: boolean; index?: number | string } = {},
    ) => {
        const {
            props: { value: selectedValue, currentValue, dataTest },
            _handleClick,
        } = this;
        const { key, divider, value, text, ...props } = itemProps;

        return divider ? (
            <SemanticDropdown.Divider key={index} />
        ) : (
            <SemanticDropdown.Item
                data-test={`${dataTest}-item`}
                className={css('item', { header })}
                active={selectedValue === value}
                selected={selectedValue === value}
                key={key || value || index}
                text={text}
                label={
                    value === currentValue && (
                        <div className={css('labelCurrent')}>Текущий</div>
                    )
                }
                value={value}
                onClick={_handleClick}
                {...props}
            />
        );
    };

    _getClassName = () => {
        // @ts-ignore
        return classNames(
            css('root', this.props.className),
            'selection', // should pass selection as class instead as prop because of props conflicts
        );
    };

    _renderOption = (item: DropdownItemProps, index: number) => {
        if (item.hidden) {
            return null;
        }
        if (!item.items) {
            return this._renderSimpleItem(item, { index });
        }

        return (
            <Fragment key={`fragment-${item.key}`}>
                {index > 0 && (
                    <SemanticDropdown.Divider className={css('divider')} />
                )}

                {this.renderHeaderOption(item)}

                {item.items.map((subItem, subIndex) =>
                    this._renderSimpleItem(subItem, {
                        index: `${index}_${subIndex}`,
                    }),
                )}
            </Fragment>
        );
    };

    renderHeaderOption = (item: DropdownItemProps) => {
        if (item.value == null) {
            return (
                <SemanticDropdown.Header
                    className={styles.itemHeader}
                    key={item.key}
                >
                    <div>{item.text}</div>
                    <div className={styles.description}>{item.description}</div>
                </SemanticDropdown.Header>
            );
        }
        return this._renderSimpleItem(
            {
                ...item,
                text: <b>{item.text}</b>,
            },
            {
                header: true,
            },
        );
    };

    render() {
        const {
            options,
            excludedKeys,
            value: selectedValue,
            selectedValueCustomRender,
            currentValue,
            className,
            dataTest,
            ...dropdownProps
        } = this.props;

        const filteredOptions = options?.filter(
            (option) => !excludedKeys?.find((value) => value === option.key),
        );

        return (
            <SemanticDropdown
                {...dropdownProps}
                className={this._getClassName()}
                trigger={this._renderTrigger(selectedValue)}
            >
                <SemanticDropdown.Menu>
                    {filteredOptions?.map(this._renderOption)}
                </SemanticDropdown.Menu>
            </SemanticDropdown>
        );
    }
}
